import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  Input,
  List,
  ListItem,
  FormControl,
  FormLabel,
  VStack,
  InputGroup,
  InputRightElement,
  IconButton,
  useOutsideClick,
  Text,
  HStack,
  Badge,
  Portal,
} from '@chakra-ui/react';
import { ChevronDownIcon, SmallCloseIcon } from '@chakra-ui/icons';

interface CategoryOption {
  primary: string;
  detailed: string;
}

interface CategorySelectorProps {
  initialPrimary?: string;
  initialDetailed?: string;
  availableCategories: CategoryOption[];
  onChange: (primary: string, detailed: string) => void;
}

const AutocompleteInput = ({
  label,
  value,
  onChange,
  options,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (val: string) => void;
  options: string[];
  placeholder?: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState<string[]>([]);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const inputGroupRef = useRef<HTMLDivElement>(null);

  useOutsideClick({
    ref: ref,
    handler: () => setIsOpen(false),
  });

  useEffect(() => {
    if (!value) {
      setFilteredOptions(options);
    } else {
      const lower = value.toLowerCase();
      setFilteredOptions(options.filter((opt) => opt.toLowerCase().includes(lower)));
    }
  }, [value, options]);

  // Recalculate dropdown position when open
  const updateDropdownPos = useCallback(() => {
    if (inputGroupRef.current) {
      const rect = inputGroupRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    updateDropdownPos();
    // Update position on scroll/resize so the dropdown follows the input
    const handler = () => updateDropdownPos();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
  }, [isOpen, updateDropdownPos]);

  const handleFocus = () => {
    setIsOpen(true);
  };

  const handleSelect = (opt: string) => {
    onChange(opt);
    setIsOpen(false);
  };

  return (
    <FormControl ref={ref}>
      <FormLabel fontSize="sm" color="gray.500" mb={1}>{label}</FormLabel>
      <InputGroup ref={inputGroupRef}>
        <Input
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setIsOpen(true);
          }}
          onFocus={handleFocus}
          placeholder={placeholder}
          bg="bg.surface"
          borderColor="xacto.borderGray"
          _focus={{ borderColor: 'xacto.electricCyan', boxShadow: 'none' }}
        />
        <InputRightElement>
            {value ? (
                <IconButton
                    aria-label="Clear"
                    icon={<SmallCloseIcon />}
                    size="xs"
                    variant="ghost"
                    onClick={() => { onChange(''); setIsOpen(true); }}
                />
            ) : (
                <ChevronDownIcon color="gray.500" />
            )}
        </InputRightElement>
      </InputGroup>

      {isOpen && filteredOptions.length > 0 && dropdownPos && (
        <Portal>
          <List
            position="fixed"
            top={`${dropdownPos.top}px`}
            left={`${dropdownPos.left}px`}
            width={`${dropdownPos.width}px`}
            zIndex={9999}
            bg="bg.primary"
            borderColor="xacto.borderGray"
            borderWidth="1px"
            borderRadius="md"
            maxH="200px"
            overflowY="auto"
            boxShadow="lg"
          >
            {filteredOptions.map((opt, idx) => (
              <ListItem
                key={idx}
                px={3}
                py={2}
                cursor="pointer"
                _hover={{ bg: 'xacto.mutedNavy' }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(opt);
                }}
              >
                <Text fontSize="sm">{opt}</Text>
              </ListItem>
            ))}
          </List>
        </Portal>
      )}
      {isOpen && value && filteredOptions.length === 0 && dropdownPos && (
        <Portal>
          <Box
            position="fixed"
            top={`${dropdownPos.top}px`}
            left={`${dropdownPos.left}px`}
            width={`${dropdownPos.width}px`}
            zIndex={9999}
            p={2}
            bg="bg.primary"
            border="1px solid"
            borderColor="gray.600"
            borderRadius="md"
          >
            <Text fontSize="xs" color="orange.300">New category will be created</Text>
          </Box>
        </Portal>
      )}
    </FormControl>
  );
};

export const CategorySelector: React.FC<CategorySelectorProps> = ({
  initialPrimary = '',
  initialDetailed = '',
  availableCategories,
  onChange,
}) => {
  const [primary, setPrimary] = useState(initialPrimary);
  const [detailed, setDetailed] = useState(initialDetailed);

  // Derive unique primaries
  const primaryOptions = useMemo(() => {
    return Array.from(new Set(availableCategories.map((c) => c.primary))).sort();
  }, [availableCategories]);

  // Derive detailed options based on selected primary
  const detailedOptions = useMemo(() => {
    if (!primary) {
        // If no primary selected, show all unique detailed? Or better, wait for primary.
        // Let's show all unique detailed just in case they want to search by detailed first (though logic implies hierarchy).
        // Actually, hierarchy is strictly Primary -> Detailed.
        // But user might want to invent a new primary but reuse detailed? Rare.
        // Let's filter by primary if set.
        return [];
    }
    return Array.from(
      new Set(
        availableCategories
          .filter((c) => c.primary === primary)
          .map((c) => c.detailed)
      )
    ).sort();
  }, [availableCategories, primary]);

  useEffect(() => {
    onChange(primary, detailed);
  }, [primary, detailed]);

  return (
    <VStack spacing={4} align="stretch">
      <AutocompleteInput
        label="Category (Primary)"
        value={primary}
        onChange={(val) => {
            setPrimary(val);
            // If primary changes, detailed might need reset if it doesn't belong?
            // User might want to keep it. Let's keep it.
        }}
        options={primaryOptions}
        placeholder="e.g. Food and Drink"
      />
      <AutocompleteInput
        label="Subcategory (Detailed)"
        value={detailed}
        onChange={setDetailed}
        options={detailedOptions}
        placeholder="e.g. Restaurants"
      />
      
      {primary && detailed && (
          <HStack>
              <Text fontSize="xs" color="gray.500">Result:</Text>
              <Badge colorScheme="cyan" variant="outline">{primary}</Badge>
              <Text fontSize="xs" color="gray.500">/</Text>
              <Badge colorScheme="cyan" variant="solid">{detailed}</Badge>
          </HStack>
      )}
    </VStack>
  );
};
